// Generated by Claude (Claude Code) by hoisting the IssueCard component out of
// IssueBoardPage's render body, where its identity changed on every render and
// remounted every card (so focus restore always targeted a detached node).
// Conversation: https://claude.ai/code/session_018qcqzEaBiPmP1GQv1wWoop
// Restyled 2026-10-12 to the DNA 1a dense list row (search-first public board).
import { ChevronRight, ThumbsUp, Users, Wrench } from "lucide-react";
import { format } from "date-fns";
import type { Issue, Status } from "@workspace/api-client-react";
import { statusPillStyle } from "@/lib/status-presentation";

export default function IssueCard({
  issue,
  status,
  categoryName,
  onOpen,
  onUpvote,
}: {
  issue: Issue;
  status: Status | undefined;
  categoryName: string | undefined;
  onOpen: (issue: Issue, trigger: HTMLElement) => void;
  onUpvote: (issue: Issue) => void;
}) {
  const pill = statusPillStyle(status?.key);
  const affected = issue.affectedCount ?? 0;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => onOpen(issue, e.currentTarget)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(issue, e.currentTarget);
        }
      }}
      aria-label={`Open issue: ${issue.title}`}
      className="dna-issue-row"
      data-testid={`card-issue-${issue.id}`}
    >
      <span className="dna-status-pill" style={pill}>
        {status?.label ?? "—"}
      </span>
      <div className="flex min-w-0 flex-col gap-1 md:col-start-2">
        <span className="text-[15.5px] font-semibold leading-snug text-[var(--dna-ink)]">
          {issue.title}
        </span>
        <span className="text-[13px] text-[var(--dna-slate-500)]">
          {categoryName ?? "Uncategorized"}
          {" · "}
          <span className="dna-mono">#{issue.id}</span>
          {" · Updated "}
          {format(new Date(issue.updatedAt), "MMM d")}
        </span>
      </div>
      <div className="flex items-center md:justify-self-end">
        {issue.hasWorkaround ? (
          <span
            title="A workaround is available for this issue"
            className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--dna-indigo-700)]"
            data-testid={`badge-workaround-${issue.id}`}
          >
            <Wrench className="h-3.5 w-3.5" />
            Workaround
          </span>
        ) : (
          <span className="hidden md:block" />
        )}
      </div>
      <div className="flex items-center justify-between gap-2 md:col-start-4 md:justify-end">
        <span
          className="dna-mono inline-flex items-center gap-1.5 text-sm text-[var(--dna-slate-700)]"
          aria-label={`${affected} carriers affected`}
          data-testid={`text-affected-${issue.id}`}
        >
          <Users className="h-3.5 w-3.5 text-[var(--dna-slate-500)]" />
          {affected}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onUpvote(issue);
          }}
          className="dna-btn-secondary h-7 min-h-7 shrink-0 whitespace-nowrap px-2 text-[11px]"
          data-testid={`button-upvote-${issue.id}`}
          aria-label={`This affects me: ${issue.title}`}
        >
          <ThumbsUp className="h-3 w-3 shrink-0" />
          This affects me
        </button>
      </div>
      <ChevronRight className="hidden h-4 w-4 text-[var(--dna-slate-400)] md:block md:col-start-5" />
    </div>
  );
}
